import { useMemo } from 'react';
import styled from 'styled-components';

import { Dimensions } from 'components/insights/constants';
import DataTable from 'components/shared/DataTable';
import { useNetworkPerformanceStore } from 'stores/networkPerformanceStore';

import { PROVIDER_PERFORMANCE_BASE_PARAMS } from '../constants';
import NetworkPerformanceApiExport from '../NetworkPerformanceApiExport';

import { buildEntityBreakdownColumns, buildEntityBreakdownRows, ENTITY_DEFAULT_SORT } from './entityBreakdownColumns';
import { type EntitySlideoutAnalyticsResult } from './useEntitySlideoutAnalytics';

export const slugify = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-');

type Props = EntitySlideoutAnalyticsResult & {
  entityId: string;
  entityName: string;
};

const EXPORT_PARAMS = {
  ...PROVIDER_PERFORMANCE_BASE_PARAMS,
  dimensions: [Dimensions.GROUP_NAME],
};

export default function EntityBreakdownTable({ entityId, entityName, dischargeRollups, loading }: Props) {
  const selectedGroupType = useNetworkPerformanceStore((s) => s.selectedGroupType);
  const groupTypeName = selectedGroupType!.displayName;

  const columns = useMemo(() => buildEntityBreakdownColumns(groupTypeName), [groupTypeName]);

  const data = useMemo(() => buildEntityBreakdownRows(dischargeRollups), [dischargeRollups]);

  const extraFilters = useMemo(
    () => ({ providerClient: [{ id: entityId, name: entityName }] }),
    [entityId, entityName]
  );

  return (
    <Wrapper>
      <TitleRow>
        <TableTitle>{groupTypeName} Breakdown</TableTitle>
        <NetworkPerformanceApiExport
          params={EXPORT_PARAMS}
          filename={`post-discharge-entity-breakdown-${slugify(entityName)}`}
          extraFilters={extraFilters}
          disabled={loading}
        />
      </TitleRow>
      <DataTable
        columns={columns}
        data={data}
        loading={loading}
        freezeFirstColumn
        hasFooter={false}
        defaultSortBy={[ENTITY_DEFAULT_SORT]}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  td:first-child:not(:last-child),
  th:first-child {
    border-right: 1px solid ${({ theme }) => theme.colors.black10};
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const TableTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.colors.black};
`;
